import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    ApDropdown,
    ApDropdownItem,
} from '@uipath/portal-shell-react';
import React, { useMemo } from 'react';
import type {
    ControllerRenderProps,
    FieldError,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import type {
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
    LlmConnectorType,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    ByoFormErrorTypes,
    ByoModelIntegrationServiceConnectorTypes,
    IntegrationServiceConnectorTypesToAuthConnectorTypes,
    IntegrationServiceConnectorTypesToLlmConnectorTypes,
    ModelFamily,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    getApiFlavorsForModel,
    getAvailableDialectsForModel,
    getLlmConnectorTypeIntegrationServiceEquivalent,
    isAiGatewayCustomAuthConnector,
} from '../../../../services/llm-gateway/LlmGateway.utils';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';
import { AiGatewayDialectDropdown } from './AiGatewayDialectDropdown';
import { AvailableModelsAutocomplete } from './AvailableModelsAutocomplete';
import { ConnectionDropdown } from './ConnectionsDropdown';
import { ConnectorDropdown } from './ConnectorDropdown';
import { LlmApiFlavorDropdown } from './LlmApiFlavorDropdown';

const useStyles = makeStyles(theme =>
    createStyles({
        llmConfigurationsSection: {
            width: '400px',
            borderLeft: `2px solid ${theme.palette.semantic.colorBackgroundDisabled}`,
        },
    }),
);

type AnyModelConfigurationSectionProps = {
    organizationId: string;
    tenantId: string;
    folderKey?: string;
    configurationMode?: string;
    useAiGateway?: boolean;
    enableByoForPassthrough: boolean;
    llmConfigurationsField: ControllerRenderProps<AddEditConfigurationFormData, 'llmConfigurations'>;
    notifyFieldChanges: () => void;
    replaceableLlms: string[];
    llmToConnectorTypeMapping: Record<string, LlmConnectorType[]>;
    byoAiGatewayCustomConnectors: string[];
    product: string;
    feature: string;
    productConfigurations?: Record<string, Record<string, ByoOperationGroupConfiguration>>;
    error?: FieldError;
    readOnly?: boolean;
};

export const AnyModelConfigurationSection: React.FC<AnyModelConfigurationSectionProps> = ({
    organizationId,
    tenantId,
    folderKey,
    configurationMode,
    useAiGateway,
    enableByoForPassthrough,
    llmConfigurationsField,
    notifyFieldChanges,
    replaceableLlms,
    llmToConnectorTypeMapping,
    byoAiGatewayCustomConnectors,
    product,
    feature,
    productConfigurations,
    error,
    readOnly,
}: AnyModelConfigurationSectionProps) => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const enableAllConnectorsForByoModel = useFeatureFlagValue(Features.EnableAllConnectorsForAiTrustLayerByoModel.name);
    const llmConfigurationField = Object.values(llmConfigurationsField.value)[0];

    const availableApiFlavors = useMemo(() => {
        if (!enableByoForPassthrough) {
            return [];
        }
        return configurationMode === 'replace'
            ? getApiFlavorsForModel(
                productConfigurations,
                product,
                feature,
                Object.keys(llmConfigurationsField.value)[0],
                false
            )
            : getApiFlavorsForModel(
                productConfigurations,
                product,
                feature,
                undefined,
                true
            );
    }, [ enableByoForPassthrough, configurationMode, productConfigurations, product, feature, llmConfigurationsField.value ]);

    const modelOverLapError = useMemo(() => {
        if (error?.type === ByoFormErrorTypes.ModelNameOverlap) {
            return error;
        }

        return undefined;
    }, [ error ]);

    return (
        <UiStack
            className={classes.llmConfigurationsSection}
            gap={SpacingToken.S}
            pl={SpacingToken.M}
            w='100%'
            direction='column'
        >
            <UiStack
                wrap='wrap'
                gap={SpacingToken.M}
                direction='row'
            >
                {configurationMode === 'replace' ? (
                    <ApDropdown
                        style={{ width: '350px' }}
                        label={translate({ id: 'CLIENT_BYO_LLM_REPLACED_MODEL' })}
                        data-cy="desired-model-dropdown"
                        disabled={readOnly}
                        required
                        selectedValue={Object.keys(llmConfigurationsField.value)[0]}
                        onSelectedValueChanged={(event) => {
                            const desiredModel = event.detail.toString();
                            const llmConfiguration: Partial<ByoLlmConfiguration> = { desiredModel };

                            if (useAiGateway) {
                                llmConfiguration.gatewayParameters = {
                                    apiUrl: '',
                                    authConnectionId: '',
                                    authConnectorType: '',
                                    vendorApiType: ModelFamily.OpenAi,
                                };
                            }

                            llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                            notifyFieldChanges();
                        }}>
                        {replaceableLlms.map(model => (
                            <ApDropdownItem
                                label={model}
                                value={model}
                                key={`ap-dropdown-item-${model}`}
                            />))}
                    </ApDropdown>
                ) : (
                    <TextField
                        style={{ width: '350px' }}
                        label={translate({ id: 'CLIENT_BYO_LLM_CONNECTED_MODEL_ALIAS' })}
                        data-cy="customer-model-alias-text-field"
                        disabled={readOnly}
                        required
                        value={Object.keys(llmConfigurationsField.value)[0]}
                        error={!!modelOverLapError}
                        helperText={modelOverLapError?.message}
                        onBlur={llmConfigurationsField.onBlur}
                        onChange={(event) => {
                            const llmConfiguration = llmConfigurationField ?? {};
                            const desiredModel = event.target.value ?? '';
                            llmConfiguration.desiredModel = desiredModel;
                            llmConfiguration.defaultModel = desiredModel;
                            llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                            notifyFieldChanges();
                        }} />
                )}
                {useAiGateway && !(enableByoForPassthrough && availableApiFlavors.length > 0) && (
                    <AiGatewayDialectDropdown
                        value={llmConfigurationField?.gatewayParameters?.vendorApiType}
                        disabled={!!readOnly || (!Object.keys(llmConfigurationsField.value)[0])}
                        availableDialects={configurationMode === 'replace' && Object.keys(llmConfigurationsField.value)[0]
                            ? getAvailableDialectsForModel(Object.keys(llmConfigurationsField.value)[0], llmToConnectorTypeMapping)
                            : undefined}
                        llmConfigurationsField={llmConfigurationsField}
                        notifyFieldChanges={notifyFieldChanges}
                        desiredModel={Object.keys(llmConfigurationsField.value)[0]}
                        llmConfiguration={llmConfigurationField}
                    />)}
                {enableByoForPassthrough && availableApiFlavors.length > 0 && (
                    <LlmApiFlavorDropdown
                        value={llmConfigurationField?.apiFlavor}
                        disabled={!!readOnly || (useAiGateway
                            ? !llmConfigurationField?.gatewayParameters?.authConnectorType
                            : !llmConfigurationField?.connectorType)}
                        availableApiFlavors={availableApiFlavors}
                        llmConfigurationsField={llmConfigurationsField}
                        notifyFieldChanges={notifyFieldChanges}
                        desiredModel={Object.keys(llmConfigurationsField.value)[0]}
                        llmConfiguration={llmConfigurationField}
                    />
                )}
            </UiStack>
            <UiStack
                wrap='wrap'
                gap={SpacingToken.M}
                direction='row'>
                <ConnectorDropdown
                    organizationId={organizationId}
                    tenantId={tenantId}
                    label={useAiGateway
                        ? translate({ id: 'CLIENT_CG_AUTH_CONNECTOR' })
                        : translate({ id: 'CLIENT_CG_CONNECTOR' })}
                    required
                    disabled={!!readOnly || (configurationMode === 'replace' && Object.keys(llmConfigurationsField.value).length === 0)}
                    filter={connectorKey => {
                        if (useAiGateway) {
                            return connectorKey in IntegrationServiceConnectorTypesToAuthConnectorTypes
                                || (isAiGatewayCustomAuthConnector(connectorKey))
                                || byoAiGatewayCustomConnectors.includes(connectorKey);
                        }

                        if (configurationMode === 'replace' && Object.keys(llmConfigurationsField.value).length === 0) {
                            return true;
                        }

                        const isLlmVendorConnector = connectorKey in IntegrationServiceConnectorTypesToLlmConnectorTypes;

                        if (!isLlmVendorConnector) {
                            return false;
                        }

                        if (configurationMode === 'replace') {
                            return llmToConnectorTypeMapping[Object.keys(llmConfigurationsField.value)[0]]
                                .includes(IntegrationServiceConnectorTypesToLlmConnectorTypes[connectorKey]);
                        }

                        return enableAllConnectorsForByoModel || ByoModelIntegrationServiceConnectorTypes.includes(connectorKey);
                    }}
                    value={
                        useAiGateway
                            ? llmConfigurationField?.gatewayParameters?.authConnectorType
                            : getLlmConnectorTypeIntegrationServiceEquivalent(llmConfigurationField?.connectorType)
                    }
                    onChange={(value) => {
                        const [ desiredModel, llmConfiguration ] = Object.entries(llmConfigurationsField.value)[0];
                        if (useAiGateway) {
                            llmConfiguration.gatewayParameters = {
                                apiUrl: '',
                                authConnectionId: '',
                                authConnectorType: value ?? '',
                                vendorApiType: llmConfiguration.gatewayParameters?.vendorApiType ?? ModelFamily.OpenAi,
                            };
                        } else {
                            llmConfiguration.connectorType = IntegrationServiceConnectorTypesToLlmConnectorTypes[value ?? ''];
                            llmConfiguration.connectionId = '';
                        }

                        llmConfiguration.customerModel = '';
                        llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                        notifyFieldChanges();
                    }} />
                <ConnectionDropdown
                    organizationId={organizationId}
                    tenantId={tenantId}
                    folderKey={folderKey}
                    connectorKey={
                        useAiGateway
                            ? llmConfigurationField?.gatewayParameters?.authConnectorType
                            : getLlmConnectorTypeIntegrationServiceEquivalent(llmConfigurationField?.connectorType)
                    }
                    label={translate({ id: 'CLIENT_CG_CONNECTION' })}
                    required
                    disabled={
                        !!readOnly || (useAiGateway
                            ? !llmConfigurationField?.gatewayParameters?.authConnectorType
                            : !llmConfigurationField?.connectorType)
                    }
                    value={
                        useAiGateway
                            ? llmConfigurationField?.gatewayParameters?.authConnectionId
                            : llmConfigurationField?.connectionId
                    }
                    onChange={(value) => {
                        const [ desiredModel, llmConfiguration ] = Object.entries(llmConfigurationsField.value)[0];
                        if (useAiGateway) {
                            llmConfiguration.gatewayParameters = {
                                apiUrl: llmConfiguration.gatewayParameters?.apiUrl ?? '',
                                authConnectionId: value.id,
                                authConnectorType: llmConfiguration.gatewayParameters?.authConnectorType ?? '',
                                vendorApiType: llmConfiguration.gatewayParameters?.vendorApiType ?? ModelFamily.OpenAi,
                            };
                        } else {
                            llmConfiguration.connectionId = value.id;
                        }
                        llmConfiguration.customerModel = '';
                        llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                        notifyFieldChanges();
                    }} />
                {useAiGateway && (
                    <TextField
                        style={{ width: '300px' }}
                        label={translate({ id: 'CLIENT_BYO_LLM_BASE_URL' })}
                        data-cy="base-url-text-field"
                        required
                        disabled={!!readOnly || !llmConfigurationField?.gatewayParameters?.authConnectionId}
                        value={llmConfigurationField?.gatewayParameters?.apiUrl}
                        onChange={(event) => {
                            const [ desiredModel, llmConfiguration ] = Object.entries(llmConfigurationsField.value)[0];
                            llmConfiguration.gatewayParameters = {
                                apiUrl: event.target.value,
                                authConnectionId: llmConfiguration?.gatewayParameters?.authConnectionId ?? '',
                                authConnectorType: llmConfiguration?.gatewayParameters?.authConnectorType ?? '',
                                vendorApiType: llmConfiguration.gatewayParameters?.vendorApiType ?? ModelFamily.OpenAi,
                            };
                            llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                            notifyFieldChanges();
                        }} />
                )}
                <AvailableModelsAutocomplete
                    label={translate({ id: 'CLIENT_BYO_LLM_CONNECTED_MODEL_ID' })}
                    required
                    disabled={
                        !!readOnly || (useAiGateway
                            ? !llmConfigurationField?.gatewayParameters?.authConnectionId
                            : !llmConfigurationField?.connectionId)
                    }
                    organizationId={organizationId}
                    tenantId={tenantId}
                    byoLlmConfiguration={llmConfigurationField}
                    initialValue={llmConfigurationField?.customerModel ?? ''}
                    onChange={(value) => {
                        const [ desiredModel, llmConfiguration ] = Object.entries(llmConfigurationsField.value)[0];
                        llmConfiguration.customerModel = value ?? '';
                        llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                        notifyFieldChanges();
                    }} />
            </UiStack>
        </UiStack>
    );
};
